/**
 * Detects when a newly deployed version of the web app takes over this tab.
 *
 * The provider service worker activates new versions immediately (skipWaiting +
 * clients.claim), but an open tab keeps running the JavaScript it already loaded
 * until it reloads, and browsers only check for a new worker on navigation. So an
 * all-day dashboard would never pick up a deployed fix. This module checks for a
 * new worker periodically and when the tab becomes visible again, and reports
 * when a new version has taken control of a tab that already had one.
 */

export const APP_UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

export interface AppUpdateEnvironment {
  serviceWorker: Pick<ServiceWorkerContainer, "controller" | "ready" | "addEventListener" | "removeEventListener">;
  document: Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener">;
  setInterval: (callback: () => void, ms: number) => unknown;
  clearInterval: (handle: unknown) => void;
}

function browserEnvironment(): AppUpdateEnvironment | null {
  if (typeof window === "undefined" || typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  return {
    serviceWorker: navigator.serviceWorker,
    document,
    setInterval: (callback, ms) => window.setInterval(callback, ms),
    clearInterval: (handle) => window.clearInterval(handle as number),
  };
}

/** Calls `onUpdate` once a new version controls this tab. Returns a cleanup function. */
export function watchForAppUpdate(
  onUpdate: () => void,
  environment: AppUpdateEnvironment | null = browserEnvironment(),
): () => void {
  if (!environment) return () => {};
  const { serviceWorker } = environment;
  // A first install also fires controllerchange (clients.claim); that is not an
  // update, so only a tab that was already controlled can be "out of date".
  let wasControlled = Boolean(serviceWorker.controller);
  let notified = false;
  let stopped = false;

  const handleControllerChange = () => {
    if (stopped) return;
    if (wasControlled && !notified) {
      notified = true;
      onUpdate();
    }
    wasControlled = true;
  };

  const checkForUpdate = () => {
    void serviceWorker.ready
      .then((registration) => registration.update())
      .catch(() => {
        // Offline or the worker is unavailable; the next check will retry.
      });
  };

  const handleVisibilityChange = () => {
    if (environment.document.visibilityState === "visible") checkForUpdate();
  };

  serviceWorker.addEventListener("controllerchange", handleControllerChange);
  environment.document.addEventListener("visibilitychange", handleVisibilityChange);
  const interval = environment.setInterval(checkForUpdate, APP_UPDATE_CHECK_INTERVAL_MS);

  return () => {
    stopped = true;
    serviceWorker.removeEventListener("controllerchange", handleControllerChange);
    environment.document.removeEventListener("visibilitychange", handleVisibilityChange);
    environment.clearInterval(interval);
  };
}
